"use client"

import React, { useState, useEffect } from 'react';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiSearch, 
  FiX, 
  FiBox, 
  FiPackage, 
  FiTruck, 
  FiHome, 
  FiTag, 
  FiCheckCircle, 
  FiMessageSquare,
  FiDollarSign,
  FiChevronRight,
  FiAlertTriangle,
  FiClock
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  getClientInventory, 
  createInventoryItem, 
  updateInventoryItem, 
  deleteInventoryItem,
  getInventoryStats
} from '../../services/inventoryService';

const categoryIcons = {
  meubles: <FiHome className="w-5 h-5" />,
  appareils_electromenagers: <FiBox className="w-5 h-5" />,
  vetements: <FiTag className="w-5 h-5" />,
  vaisselle: <FiPackage className="w-5 h-5" />,
  objets_de_valeur: <FiTag className="w-5 h-5" />,
  autres: <FiBox className="w-5 h-5" />
};

const statusColors = {
  en_stock: 'bg-green-100 text-green-800',
  en_transit: 'bg-blue-100 text-blue-800',
  perdu: 'bg-red-100 text-red-800',
  endommage: 'bg-yellow-100 text-yellow-800'
};

const statusLabels = {
  en_stock: 'En stock',
  en_transit: 'En transit',
  perdu: 'Perdu',
  endommage: 'Endommagé'
};

const categoryLabels = {
  meubles: 'Meubles',
  appareils_electromenagers: 'Électroménager',
  vetements: 'Vêtements',
  vaisselle: 'Vaisselle',
  objets_de_valeur: 'Objets de valeur',
  autres: 'Autres'
};

const Inventory = () => {
  const [inventory, setInventory] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all'
  });
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [stats, setStats] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    quantity: 1,
    category: 'meubles',
    status: 'en_stock',
    estimated_value: '',
    notes: ''
  });

  // Charger l'inventaire
  useEffect(() => {
    const loadInventory = async () => {
      try {
        setLoading(true);
        const data = await getClientInventory();
        setInventory(data);
        setFilteredItems(data);
        
        // Charger les statistiques
        const statsData = await getInventoryStats();
        setStats(statsData);
      } catch (err) {
        setError('Erreur lors du chargement de l\'inventaire');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    loadInventory();
  }, []);

  // Filtrer les articles
  useEffect(() => {
    let result = [...inventory];
    
    // Filtre par terme de recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        item.name.toLowerCase().includes(term) ||
        (item.description && item.description.toLowerCase().includes(term))
      );
    }
    
    // Filtre par catégorie
    if (filters.category !== 'all') {
      result = result.filter(item => item.category === filters.category);
    }
    
    // Filtre par statut
    if (filters.status !== 'all') {
      result = result.filter(item => item.status === filters.status);
    }
    
    setFilteredItems(result);
  }, [searchTerm, filters, inventory]);

  // Gérer les changements du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'quantity' || name === 'estimated_value' ? Number(value) : value
    }));
  };

  // Soumettre le formulaire (création ou mise à jour)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (editingItem) {
        // Mise à jour d'un article existant
        const updatedItem = await updateInventoryItem(editingItem.id, formData);
        setInventory(prev => 
          prev.map(item => item.id === editingItem.id ? updatedItem : item)
        );
        toast.success('Article mis à jour avec succès');
      } else {
        // Création d'un nouvel article
        const newItem = await createInventoryItem(formData);
        setInventory(prev => [newItem, ...prev]);
        toast.success('Article ajouté avec succès');
      }
      
      // Réinitialiser le formulaire
      setShowForm(false);
      setEditingItem(null);
      setFormData({
        name: '',
        description: '',
        quantity: 1,
        category: 'meubles',
        status: 'en_stock',
        estimated_value: '',
        notes: ''
      });
      
      // Mettre à jour les statistiques
      const statsData = await getInventoryStats();
      setStats(statsData);
      
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      toast.error(err.response?.data?.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  // Pré-remplir le formulaire pour édition
  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      quantity: item.quantity || 1,
      category: item.category || 'meubles',
      status: item.status || 'en_stock',
      estimated_value: item.estimated_value || '',
      notes: item.notes || ''
    });
    setShowForm(true);
  };

  // Supprimer un article
  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      try {
        setLoading(true);
        await deleteInventoryItem(id);
        setInventory(prev => prev.filter(item => item.id !== id));
        toast.success('Article supprimé avec succès');
        
        // Mettre à jour les statistiques
        const statsData = await getInventoryStats();
        setStats(statsData);
        
      } catch (err) {
        console.error('Erreur lors de la suppression:', err);
        toast.error('Erreur lors de la suppression de l\'article');
      } finally {
        setLoading(false);
      }
    }
  };

  // Gestion du focus et des événements clavier
  useEffect(() => {
    if (showForm) {
      // Empêcher le défilement du fond quand le modal est ouvert
      document.body.style.overflow = 'hidden';
      
      // Gérer la touche Escape
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          handleCancel();
        }
      };
      
      document.addEventListener('keydown', handleKeyDown);
      
      // Mettre le focus sur le premier élément interactif du modal
      const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const modal = document.querySelector('.modal-content');
      const focusableContent = modal?.querySelectorAll(focusableElements);
      if (focusableContent && focusableContent.length > 0) {
        focusableContent[0].focus();
      }
      
      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [showForm]);

  // Annuler l'édition
  const handleCancel = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setShowForm(false);
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      quantity: 1,
      category: 'meubles',
      status: 'en_stock',
      estimated_value: '',
      notes: ''
    });
  };

  // Afficher le formulaire de création
  const showAddForm = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      quantity: 1,
      category: 'meubles',
      status: 'en_stock',
      estimated_value: '',
      notes: ''
    });
    setShowForm(true);
  };

  // Calculer le nombre total d'articles
  const totalItems = inventory.reduce((sum, item) => sum + (item.quantity || 1), 0);

  // Fonction pour formater la date en français
  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifié';
    try {
      return format(new Date(dateString), 'PPP', { locale: fr });
    } catch (e) {
      console.error('Erreur de formatage de date:', e);
      return new Date(dateString).toLocaleDateString('fr-FR');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex flex-col space-y-2 sm:flex-row sm:justify-between sm:items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Mon Inventaire</h1>
              <p className="mt-1 text-sm text-gray-600">Gérez et suivez l'état de vos biens</p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setEditingItem(null);
                  setFormData({
                    name: '',
                    description: '',
                    quantity: 1,
                    category: 'meubles',
                    status: 'en_stock',
                    estimated_value: '',
                    notes: ''
                  });
                  setShowForm(true);
                }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FiPlus className="w-4 h-4 mr-2" />
                Ajouter un article
              </button>
            </div>
          </div>
          
          {/* Cartes de statistiques */}
          {stats && (
            <div className="grid grid-cols-1 gap-5 mt-6 sm:grid-cols-2 lg:grid-cols-4">
              <div className="px-4 py-5 overflow-hidden bg-white rounded-lg shadow">
                <div className="flex items-center">
                  <div className="flex-shrink-0 p-3 bg-blue-100 rounded-md">
                    <FiPackage className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-5">
                    <p className="text-sm font-medium text-gray-500 truncate">Total des articles</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">{stats.totalItems}</p>
                  </div>
                </div>
              </div>
              
              <div className="px-4 py-5 overflow-hidden bg-white rounded-lg shadow">
                <div className="flex items-center">
                  <div className="flex-shrink-0 p-3 bg-green-100 rounded-md">
                    <FiCheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-5">
                    <p className="text-sm font-medium text-gray-500 truncate">En stock</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">{stats.itemsInStock}</p>
                  </div>
                </div>
              </div>
              
              <div className="px-4 py-5 overflow-hidden bg-white rounded-lg shadow">
                <div className="flex items-center">
                  <div className="flex-shrink-0 p-3 bg-yellow-100 rounded-md">
                    <FiTruck className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="ml-5">
                    <p className="text-sm font-medium text-gray-500 truncate">En transit</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">{stats.itemsInTransit}</p>
                  </div>
                </div>
              </div>
              
              <div className="px-4 py-5 overflow-hidden bg-white rounded-lg shadow">
                <div className="flex items-center">
                  <div className="flex-shrink-0 p-3 bg-red-100 rounded-md">
                    <FiAlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="ml-5">
                    <p className="text-sm font-medium text-gray-500 truncate">Attention</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">{stats.itemsNeedingAttention}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistiques unifiées */}
        {stats && (
          <div className="grid grid-cols-1 gap-5 mb-8 sm:grid-cols-2 lg:grid-cols-4">
            <div className="px-4 py-5 overflow-hidden bg-white rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 bg-blue-100 rounded-md">
                  <FiPackage className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-5">
                  <p className="text-sm font-medium text-gray-500 truncate">Total des articles</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">{stats.totalItems || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="px-4 py-5 overflow-hidden bg-white rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 bg-green-100 rounded-md">
                  <FiCheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-5">
                  <p className="text-sm font-medium text-gray-500 truncate">En stock</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">{stats.byStatus?.en_stock || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="px-4 py-5 overflow-hidden bg-white rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 bg-amber-100 rounded-md">
                  <FiTruck className="w-6 h-6 text-amber-600" />
                </div>
                <div className="ml-5">
                  <p className="text-sm font-medium text-gray-500 truncate">En transit</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">{stats.byStatus?.en_transit || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="px-4 py-5 overflow-hidden bg-white rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 bg-purple-100 rounded-md">
                  <FiTag className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-5">
                  <p className="text-sm font-medium text-gray-500 truncate">Catégorie principale</p>
                  <p className="mt-1 text-lg font-semibold text-gray-900 capitalize">
                    {categoryLabels[stats.mostCommonCategory?.name] || 'Aucune'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Barre de recherche et filtres */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiSearch className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Rechercher un article..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            
            <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-3">
              <div className="relative">
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                  className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="all">Toutes les catégories</option>
                  {Object.entries(categoryLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div className="relative">
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="all">Tous les statuts</option>
                  {Object.entries(statusLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              {(filters.category !== 'all' || filters.status !== 'all' || searchTerm) && (
                <button
                  onClick={() => {
                    setFilters({ category: 'all', status: 'all' });
                    setSearchTerm('');
                  }}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FiX className="mr-1" /> Réinitialiser
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Liste des articles */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {loading && !inventory.length ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-gray-600">Chargement de votre inventaire...</p>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Réessayer
              </button>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-8 text-center">
              <FiPackage className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun article trouvé</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || filters.category !== 'all' || filters.status !== 'all' 
                  ? 'Aucun article ne correspond à vos critères de recherche.'
                  : 'Commencez par ajouter votre premier article.'}
              </p>
              <div className="mt-6 space-x-3">
                <button
                  onClick={showAddForm}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FiPlus className="-ml-1 mr-2 h-5 w-5" />
                  Ajouter un article
                </button>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {filteredItems.map((item) => (
                <li key={item.id} className="hover:bg-gray-50">
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                          {categoryIcons[item.category] || <FiBox className="h-5 w-5" />}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          <div className="text-sm text-gray-500">
                            {categoryLabels[item.category] || 'Autre'} • {item.quantity || 1} unité{item.quantity > 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[item.status] || 'bg-gray-100 text-gray-800'}`}>
                          {statusLabels[item.status] || item.status}
                        </span>
                        
                        {item.estimated_value && (
                          <span className="text-sm text-gray-900">
                            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(item.estimated_value)}
                            {item.quantity > 1 && ` (${item.quantity} × ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(item.estimated_value / item.quantity)})`}
                          </span>
                        )}
                        
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-amber-600 hover:text-amber-900"
                        >
                          <FiEdit2 className="h-5 w-5" />
                          <span className="sr-only">Modifier</span>
                        </button>
                        
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FiTrash2 className="h-5 w-5" />
                          <span className="sr-only">Supprimer</span>
                        </button>
                      </div>
                    </div>
                    
                    {item.description && (
                      <div className="mt-2 text-sm text-gray-600">
                        {item.description.length > 150 
                          ? `${item.description.substring(0, 150)}...` 
                          : item.description}
                      </div>
                    )}
                    
                    <div className="mt-2 flex justify-between text-xs text-gray-500">
                      <div>
                        Ajouté le {new Date(item.created_at).toLocaleDateString('fr-FR')}
                      </div>
                      {item.notes && (
                        <div className="text-amber-600">
                          <FiMessageSquare className="inline-block mr-1" />
                          Note
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Formulaire modal */}
      <AnimatePresence>
        {showForm && (
          <div 
            className="fixed inset-0 z-50 overflow-y-auto" 
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
            onClick={handleCancel}
          >
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div 
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                aria-hidden="true"
              ></div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                &#8203;
              </span>

              <motion.div 
                className="modal-content inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full relative"
                role="document"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
                tabIndex="-1"
              >
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 p-1"
                    onClick={handleCancel}
                    aria-label="Fermer la fenêtre"
                  >
                    <FiX className="h-6 w-6" />
                  </button>
                </div>

                <div className="px-6 pt-5 pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                        {editingItem ? 'Modifier l\'article' : 'Ajouter un article'}
                      </h3>
                      <div className="mt-2">
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                              Nom de l'article <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              required
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                              value={formData.name}
                              onChange={handleInputChange}
                            />
                          </div>

                          <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                              Description
                            </label>
                            <textarea
                              id="description"
                              name="description"
                              rows="2"
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                              value={formData.description}
                              onChange={handleInputChange}
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                                Quantité
                              </label>
                              <input
                                type="number"
                                id="quantity"
                                name="quantity"
                                min="1"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                                value={formData.quantity}
                                onChange={handleInputChange}
                              />
                            </div>

                            <div>
                              <label htmlFor="estimated_value" className="block text-sm font-medium text-gray-700">
                                Valeur estimée (€)
                              </label>
                              <input
                                type="number"
                                id="estimated_value"
                                name="estimated_value"
                                step="0.01"
                                min="0"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                                value={formData.estimated_value}
                                onChange={handleInputChange}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                                Catégorie
                              </label>
                              <select
                                id="category"
                                name="category"
                                className="mt-1 block w-full border border-gray-300 bg-white rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                                value={formData.category}
                                onChange={handleInputChange}
                              >
                                {Object.entries(categoryLabels).map(([value, label]) => (
                                  <option key={value} value={value}>{label}</option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                                Statut
                              </label>
                              <select
                                id="status"
                                name="status"
                                className="mt-1 block w-full border border-gray-300 bg-white rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                                value={formData.status}
                                onChange={handleInputChange}
                              >
                                {Object.entries(statusLabels).map(([value, label]) => (
                                  <option key={value} value={value}>{label}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div>
                            <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                              Notes supplémentaires
                            </label>
                            <textarea
                              id="notes"
                              name="notes"
                              rows="2"
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                              value={formData.notes}
                              onChange={handleInputChange}
                            />
                          </div>

                          <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                            <button
                              type="submit"
                              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-base font-medium text-white hover:from-amber-600 hover:to-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 sm:col-start-2 sm:text-sm"
                              disabled={loading}
                            >
                              {loading ? 'Enregistrement...' : (editingItem ? 'Mettre à jour' : 'Ajouter')}
                            </button>
                            <button
                              type="button"
                              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                              onClick={handleCancel}
                              disabled={loading}
                            >
                              Annuler
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Inventory;
